/**
 * Atribución CTA cumpleaños → /r/cumple (sessionStorage, TTL 30 min).
 * Permite correlacionar clicks en hero/video/bio con landing y conversión en PostHog.
 */

export type CumpleCtaSource =
  | "hero_birthday_base_template"
  | "hero_birthday_base_template_black"
  | "mux_video_cta"
  | "bio_simple_video_overlay"
  | "test_cta_button"
  | "direct"
  | "unknown";

export type CumpleCtaAttribution = {
  source: CumpleCtaSource;
  source_template?: string;
  source_path: string;
  source_url: string;
  href: string;
  label?: string;
  element_id?: string;
  surface?: string;
  country_code?: string;
  province?: string;
  worker_variant?: string;
  clicked_at: string;
  ph_session_id?: string;
};

const STORAGE_KEY = "cumple_cta_attribution_v1";
const TTL_MS = 30 * 60 * 1000;

export function isCumpleDestination(url: string): boolean {
  const u = (url ?? "").trim();
  if (!u) return false;
  if (u === "/r/cumple" || u.startsWith("/r/cumple?") || u.startsWith("/r/cumple#")) {
    return true;
  }
  try {
    const parsed = new URL(u, typeof window !== "undefined" ? window.location.origin : "https://www.delfuocampo.com");
    return parsed.pathname === "/r/cumple";
  } catch {
    return u.includes("/r/cumple");
  }
}

export function storeCumpleCtaAttribution(
  attrs: Omit<CumpleCtaAttribution, "clicked_at"> & { clicked_at?: string },
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: CumpleCtaAttribution = {
      ...attrs,
      clicked_at: attrs.clicked_at ?? new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

export function readCumpleCtaAttribution(): CumpleCtaAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CumpleCtaAttribution;
    if (!parsed?.clicked_at || !parsed.source) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const age = Date.now() - new Date(parsed.clicked_at).getTime();
    if (!Number.isFinite(age) || age > TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearCumpleCtaAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getCumpleAttributionPostHogProps(): Record<string, string | number | boolean> {
  const attr = readCumpleCtaAttribution();
  if (!attr) {
    return { cumple_from_cta: false };
  }
  const secondsSinceClick = Math.max(
    0,
    Math.round((Date.now() - new Date(attr.clicked_at).getTime()) / 1000),
  );
  return {
    cumple_from_cta: true,
    cumple_cta_source: attr.source,
    cumple_cta_source_template: attr.source_template ?? "",
    cumple_cta_source_path: attr.source_path,
    cumple_cta_source_url: attr.source_url,
    cumple_cta_href: attr.href,
    cumple_cta_element_id: attr.element_id ?? "",
    cumple_cta_surface: attr.surface ?? "",
    cumple_cta_country: attr.country_code ?? "",
    cumple_cta_province: attr.province ?? "",
    cumple_cta_worker_variant: attr.worker_variant ?? "",
    cumple_cta_seconds_since_click: secondsSinceClick,
  };
}